import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { formattedUserFacingMonthDayYearLong } from 'DateHelpers';
import { Text } from 'Text';
import { InlineLink } from 'InlineLink';
import { type FileUpdatedAt, type UploadedDocument } from '../editDocuments.types';

// An upload response gives epoch seconds; a document read off the current user already holds a Date,
// which would be NaN if multiplied. useDateThreshold=false matches the old template's amDateFormat.
function uploadedOn(fileUpdatedAt: FileUpdatedAt) {
    const date = typeof fileUpdatedAt === 'number' ? new Date(fileUpdatedAt * 1000) : new Date(fileUpdatedAt);
    return formattedUserFacingMonthDayYearLong(date, false);
}

// The list of already-uploaded documents under an upload button. `onView` is only supplied where the
// old UI made the file name clickable (transcripts), not for English proficiency documents.
export function UploadedDocumentList({
    documents,
    onView,
}: {
    documents: UploadedDocument[];
    onView?: (document: UploadedDocument) => void;
}) {
    const { t } = useTranslation('back_royal');

    if (documents.length === 0) return null;

    // `list-none ps-0` overrides the disc and indent the app's global styles put on every ul.
    return (
        <ul className="flex min-w-0 list-none flex-col gap-2 ps-0">
            {documents.map(document => (
                <li key={document.id} className="flex min-w-0 flex-col">
                    {onView ? (
                        // `self-start text-start` stops the underlying full-width block button from
                        // centring the file name.
                        <InlineLink
                            className="self-start break-all text-start text-sm font-semibold leading-tight"
                            onPress={() => onView(document)}
                        >
                            {document.fileFileName}
                            <FontAwesomeIcon icon={faExternalLink} className="ms-1" />
                        </InlineLink>
                    ) : (
                        <Text as="div" color="black" weight="semibold" className="break-all text-sm leading-tight">
                            {document.fileFileName}
                        </Text>
                    )}
                    <Text as="div" color="slate-grey" className="text-xs">
                        {t('settings.edit_documents.uploaded_on', { uploadDate: uploadedOn(document.fileUpdatedAt) })}
                    </Text>
                </li>
            ))}
        </ul>
    );
}
